// Google Maps JavaScript API の動的ローダー (inline bootstrap loader)。
// この形式でロードすると google.maps.importLibrary() が利用可能になり、
// 「Places API (New)」の PlaceAutocompleteElement を取得できる。
//
// 2025年3月以降に作成された GCP プロジェクトでは legacy Autocomplete API は
// 提供されないため、新 PlaceAutocompleteElement のみが選択肢となる。
let sdkPromise = null;

function loadMapsSdk(apiKey) {
  if (window.google?.maps?.importLibrary) return Promise.resolve();
  if (sdkPromise) return sdkPromise;

  sdkPromise = new Promise((resolve, reject) => {
    try {
      // Google 公式の inline loader 相当 (importLibrary を window.google.maps に設置する)
      const params = { key: apiKey, v: 'weekly', language: 'ja' };
      ((g) => {
        let h, a, k;
        const p = 'The Google Maps JavaScript API', c = 'google', l = 'importLibrary', q = '__ib__';
        const m = document;
        const b = window[c] || (window[c] = {});
        const d = b.maps || (b.maps = {});
        const r = new Set();
        const u = () =>
          h ||
          (h = new Promise((f, n) => {
            a = m.createElement('script');
            const e = new URLSearchParams();
            e.set('libraries', [...r] + '');
            for (k in g) e.set(k.replace(/[A-Z]/g, (t) => '_' + t[0].toLowerCase()), g[k]);
            e.set('callback', c + '.maps.' + q);
            a.src = `https://maps.${c}apis.com/maps/api/js?` + e;
            d[q] = f;
            a.onerror = () => {
              h = null;
              n(new Error(p + ' could not load.'));
            };
            a.nonce = m.querySelector('script[nonce]')?.nonce || '';
            m.head.append(a);
          }));
        if (d[l]) {
          console.warn(p + ' only loads once. Ignoring:', g);
        } else {
          d[l] = (f, ...n) => r.add(f) && u().then(() => d[l](f, ...n));
        }
      })(params);
      resolve();
    } catch (e) {
      reject(e);
    }
  });
  return sdkPromise;
}

export async function initSearchUI({ apiKey, onCityChosen, onError }) {
  const input = document.getElementById('city-input-placeholder');
  if (input) input.placeholder = 'Maps SDK 初期化中…';

  try {
    await loadMapsSdk(apiKey);
  } catch (e) {
    if (input) input.placeholder = 'SDK 初期化失敗';
    onError?.(e.message);
    return;
  }

  let placesLib;
  try {
    placesLib = await google.maps.importLibrary('places');
  } catch (e) {
    if (input) input.placeholder = 'Places API 読込失敗';
    onError?.(
      'Places API の読込に失敗しました。GCP で「Places API (New)」を有効化してください: ' +
        (e.message ?? e)
    );
    return;
  }

  const PlaceAutocompleteElement = placesLib.PlaceAutocompleteElement;
  const LegacyAutocomplete = placesLib.Autocomplete;

  if (PlaceAutocompleteElement) {
    setupNewAutocomplete(PlaceAutocompleteElement, onCityChosen, onError);
  } else if (LegacyAutocomplete) {
    setupLegacyAutocomplete(LegacyAutocomplete, onCityChosen, onError);
  } else {
    if (input) input.placeholder = 'オートコンプリート不可';
    onError?.(
      'オートコンプリート実装が見つかりません。GCP で「Places API (New)」を有効化してください。'
    );
  }
}

function setupNewAutocomplete(PlaceAutocompleteElement, onCityChosen, onError) {
  const placeholder = document.getElementById('city-input-placeholder');
  const row = document.querySelector('.search-row');

  let ac;
  // 一部のバージョンでは (cities) ショートカットが拒否されるため、
  // 失敗したら具体的な place type にフォールバック。
  try {
    ac = new PlaceAutocompleteElement({
      includedPrimaryTypes: ['(cities)'],
    });
  } catch (e1) {
    console.warn('(cities) shortcut not accepted, falling back to specific types:', e1);
    try {
      ac = new PlaceAutocompleteElement({
        includedPrimaryTypes: ['locality', 'administrative_area_level_3'],
      });
    } catch (e2) {
      onError?.('PlaceAutocompleteElement 初期化に失敗: ' + (e2.message ?? e2));
      return;
    }
  }

  ac.id = 'city-input';
  ac.style.width = '100%';
  row.prepend(ac);
  placeholder?.remove();

  const handleSelect = async (event) => {
    try {
      const prediction = event.placePrediction ?? event.place;
      if (!prediction) {
        onError?.('候補から場所情報を取得できませんでした');
        return;
      }
      const place = prediction.toPlace ? prediction.toPlace() : prediction;
      if (place.fetchFields) {
        await place.fetchFields({ fields: ['displayName', 'location'] });
      }
      const lat = typeof place.location?.lat === 'function' ? place.location.lat() : place.location?.lat;
      const lng = typeof place.location?.lng === 'function' ? place.location.lng() : place.location?.lng;
      if (lat == null || lng == null) {
        onError?.('座標を取得できませんでした');
        return;
      }
      const name = place.displayName ?? '選択した都市';
      onCityChosen({ lat, lng, name });
    } catch (e) {
      console.error(e);
      onError?.('場所情報の取得に失敗: ' + (e.message ?? e));
    }
  };

  // バージョンによってイベント名が異なるため両方リッスン (どちらかだけ発火する)
  ac.addEventListener('gmp-select', handleSelect);
  ac.addEventListener('gmp-placeselect', handleSelect);
}

function setupLegacyAutocomplete(Autocomplete, onCityChosen, onError) {
  const input = document.getElementById('city-input-placeholder');
  if (!input) return;
  input.disabled = false;
  input.id = 'city-input';
  input.placeholder = '例: 東京, パリ, ニューヨーク';

  const ac = new Autocomplete(input, {
    types: ['(cities)'],
    fields: ['geometry', 'name'],
  });
  ac.addListener('place_changed', () => {
    const p = ac.getPlace();
    if (!p?.geometry?.location) {
      onError?.('候補リストから都市を選択してください');
      return;
    }
    onCityChosen({
      lat: p.geometry.location.lat(),
      lng: p.geometry.location.lng(),
      name: p.name ?? '選択した都市',
    });
  });
}
